import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { EmployeePhotoGrant } from '@dru/types';
import { Alert, Button, Dialog, cn } from '@dru/ui';
import { Camera, ImagePlus, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';

/**
 * The worker's photo: shown on the record, taken from a file or the webcam.
 *
 * The image never has a URL of its own. The record says `hasPhoto`; the page
 * asks for a five-minute grant, follows it, and shows the bytes from memory —
 * the same path a certificate download takes, because a photograph of a
 * named person is personal data and an <img src="https://…"> that works for
 * years is not how that is handled here.
 *
 * Capture is the browser's camera API drawn to a canvas. Whatever the source,
 * the picture is brought under the API's 1 MB limit here, by resizing, so a
 * 4 MB phone photo is accepted rather than refused.
 */

/** A picture ready to send: the API's shape, plus a preview for the screen. */
export interface PhotoShot {
  mimeType: 'image/jpeg' | 'image/png';
  /** base64, no data-URL prefix. */
  content: string;
  previewUrl: string;
}

export const PHOTO_MAX_BYTES = 1_048_576;
/** Long side after resizing. Plenty for an ID slip, small enough for a phone upload. */
const MAX_SIDE = 1024;

/** The grant, then the bytes. Returns an object URL the caller must revoke. */
export async function fetchPhoto(path: string): Promise<string> {
  const grant = await api.get<EmployeePhotoGrant>(path);
  const res = await fetch(grant.url, { credentials: 'include' });
  if (!res.ok) throw new Error('The photo could not be fetched.');
  const bytes = await res.blob();
  return URL.createObjectURL(new Blob([bytes], { type: grant.mimeType }));
}

/**
 * The photo of one employee, as an object URL, or null while loading, when
 * there is none, or when the grant could not be followed. Re-fetched when
 * `updatedAt` changes, so a replaced photo is not shown from a stale copy.
 */
export function useEmployeePhoto(
  employeeId: string | undefined,
  hasPhoto: boolean | undefined,
  updatedAt: string | null | undefined,
): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!employeeId || !hasPhoto) { setSrc(null); return; }
    let live = true;
    let url: string | null = null;
    fetchPhoto(`/employees/${employeeId}/photo`)
      .then((u) => { if (live) { url = u; setSrc(u); } else URL.revokeObjectURL(u); })
      .catch(() => { if (live) setSrc(null); });
    return () => { live = false; setSrc(null); if (url) URL.revokeObjectURL(url); };
  }, [employeeId, hasPhoto, updatedAt]);
  return src;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : '';
  return (first + last).toUpperCase() || '?';
}

const AVATAR_SIZE = {
  sm: 'h-10 w-10 text-small',
  md: 'h-14 w-14 text-h4',
  lg: 'h-20 w-20 text-h2 max-md:h-16 max-md:w-16 max-md:text-h3',
  xl: 'h-28 w-28 text-h1',
};

/** The photo, or the person's initials where there is none. */
export function EmployeeAvatar({ name, src, size = 'lg', className }: {
  name: string; src: string | null; size?: keyof typeof AVATAR_SIZE; className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
        'bg-primary-100 font-semibold text-primary-700 ring-1 ring-neutral-200',
        AVATAR_SIZE[size], className,
      )}
      aria-hidden={src ? undefined : true}
    >
      {src
        ? <img src={src} alt={`Photo of ${name}`} className="h-full w-full object-cover" />
        : initialsOf(name)}
    </span>
  );
}

/** The avatar as a control: press to change the photo. */
export function AvatarButton({ name, src, onClick, label = 'Change photo' }: {
  name: string; src: string | null; onClick: () => void; label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="group relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
    >
      <EmployeeAvatar name={name} src={src} />
      <span
        aria-hidden
        className="absolute -bottom-0.5 -right-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-neutral-600 shadow ring-1 ring-neutral-200 group-hover:text-primary-700"
      >
        <Camera className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

// ── Image preparation ───────────────────────────────────────────────

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('The file could not be read.'));
    r.onload = () => resolve(String(r.result).replace(/^data:[^;]+;base64,/, ''));
    r.readAsDataURL(blob);
  });
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file is not an image the browser can open.')); };
    img.src = url;
  });
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('The picture could not be encoded.'))), 'image/jpeg', quality);
  });
}

/**
 * Brings any image the browser can open under the limit, as a JPEG, by
 * drawing it smaller. A JPEG or PNG already under the limit is sent as it
 * is — a file somebody chose deliberately is not re-encoded for no reason.
 */
export async function prepareShot(source: Blob): Promise<PhotoShot> {
  const accepted = source.type === 'image/jpeg' || source.type === 'image/png';
  if (accepted && source.size <= PHOTO_MAX_BYTES) {
    return { mimeType: source.type as PhotoShot['mimeType'], content: await toBase64(source), previewUrl: URL.createObjectURL(source) };
  }
  const img = await loadImage(source);
  let side = MAX_SIDE;
  let quality = 0.85;
  for (let attempt = 0; attempt < 4; attempt++) {
    const scale = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('The picture could not be processed.');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await canvasBlob(canvas, quality);
    if (blob.size <= PHOTO_MAX_BYTES) {
      return { mimeType: 'image/jpeg', content: await toBase64(blob), previewUrl: URL.createObjectURL(blob) };
    }
    side = Math.round(side * 0.7);
    quality = Math.max(0.6, quality - 0.1);
  }
  throw new Error('That picture could not be made small enough. Try a different one.');
}

/** A frame from the live camera, cropped square from the centre. */
async function captureFrame(video: HTMLVideoElement): Promise<PhotoShot> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) throw new Error('The camera has not produced a frame yet. Try again.');
  const side = Math.min(w, h, 800);
  const crop = Math.min(w, h);
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('The picture could not be processed.');
  ctx.drawImage(video, (w - crop) / 2, (h - crop) / 2, crop, crop, 0, 0, side, side);
  const blob = await canvasBlob(canvas, 0.85);
  return prepareShot(blob);
}

// ── The dialog ──────────────────────────────────────────────────────

type Mode = 'choose' | 'camera' | 'preview';

/**
 * Choose a file or take one with the camera, look at it, keep it.
 *
 * `onPick` is the parent's: on the record it uploads at once; on the add
 * form it holds the picture until the person exists to attach it to.
 */
export function PhotoDialog({ name, hasPhoto, onClose, onPick, onRemove }: {
  name: string;
  hasPhoto?: boolean;
  onClose: () => void;
  onPick: (shot: PhotoShot) => Promise<void> | void;
  /** Offered only when there is a photo to remove. */
  onRemove?: () => Promise<void> | void;
}) {
  const [mode, setMode] = useState<Mode>('choose');
  const [shot, setShot] = useState<PhotoShot | null>(null);
  const [from, setFrom] = useState<'file' | 'camera'>('file');
  const [busy, setBusy] = useState<'save' | 'remove' | 'prepare' | false>(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Once handed to the parent, the preview URL is theirs to free.
  const handedOff = useRef(false);

  const cameraSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

  // A preview the dialog still owns is freed when it is replaced or dropped.
  useEffect(() => () => { if (shot && !handedOff.current) URL.revokeObjectURL(shot.previewUrl); }, [shot]);

  const take = async (make: () => Promise<PhotoShot>, source: 'file' | 'camera') => {
    setBusy('prepare'); setError(null);
    try {
      setShot(await make());
      setFrom(source);
      setMode('preview');
    } catch (err) {
      setError(errorMessage(err) || (err as Error).message);
    } finally { setBusy(false); }
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    void take(() => prepareShot(file), 'file');
  };

  const save = async () => {
    if (!shot) return;
    setBusy('save'); setError(null);
    handedOff.current = true;
    try {
      await onPick(shot);
    } catch (err) {
      handedOff.current = false;
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!onRemove) return;
    setBusy('remove'); setError(null);
    try {
      await onRemove();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  const footer: ReactNode = mode === 'preview' ? (
    <>
      <Button variant="ghost" onClick={() => { setShot(null); setMode(from === 'camera' ? 'camera' : 'choose'); }} icon={<RefreshCw className="h-4 w-4" />}>
        {from === 'camera' ? 'Retake' : 'Choose another'}
      </Button>
      <Button loading={busy === 'save'} onClick={save}>Use this photo</Button>
    </>
  ) : mode === 'camera' ? (
    <Button variant="ghost" onClick={() => setMode('choose')}>Back</Button>
  ) : (
    <>
      {hasPhoto && onRemove && (
        <Button variant="danger" loading={busy === 'remove'} onClick={remove} icon={<Trash2 className="h-4 w-4" />} className="mr-auto">
          Remove photo
        </Button>
      )}
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
    </>
  );

  return (
    <Dialog title={hasPhoto ? `Change photo — ${name}` : `Add photo — ${name}`} onClose={onClose} footer={footer}>
      <div className="space-y-4 pt-3">
        {error && <Alert tone="danger">{error}</Alert>}

        {mode === 'choose' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png"
              className="sr-only"
              aria-label="Choose a photo file"
              onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            <ChoiceButton
              icon={<ImagePlus className="h-6 w-6" />}
              title="Choose a file"
              hint="JPEG or PNG. A large photo is resized here."
              loading={busy === 'prepare'}
              onClick={() => fileRef.current?.click()}
            />
            <ChoiceButton
              icon={<Camera className="h-6 w-6" />}
              title="Use camera"
              hint={cameraSupported ? 'Take the picture now with this device.' : 'No camera is available on this device.'}
              disabled={!cameraSupported}
              onClick={() => { setError(null); setMode('camera'); }}
            />
          </div>
        )}

        {mode === 'camera' && (
          <CameraView
            busy={busy === 'prepare'}
            onCapture={(video) => take(() => captureFrame(video), 'camera')}
            onUnavailable={(why) => { setError(why); setMode('choose'); }}
          />
        )}

        {mode === 'preview' && shot && (
          <div className="flex flex-col items-center gap-2">
            <img
              src={shot.previewUrl}
              alt={`New photo of ${name}`}
              className="max-h-72 w-auto max-w-full rounded-card ring-1 ring-neutral-200"
            />
            <p className="text-caption text-neutral-500">
              Shown on the record, the printed ID slip and in {name.split(' ')[0]}’s app.
            </p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function ChoiceButton({ icon, title, hint, onClick, disabled, loading }: {
  icon: ReactNode; title: string; hint: string; onClick: () => void; disabled?: boolean; loading?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'flex min-h-24 flex-col items-start gap-1 rounded-card border border-neutral-200 p-4 text-left',
        'hover:border-primary-300 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-neutral-200 disabled:hover:bg-transparent',
      )}
    >
      <span className="text-primary-700">{icon}</span>
      <span className="font-semibold text-neutral-900">{loading ? 'Preparing…' : title}</span>
      <span className="text-caption text-neutral-500">{hint}</span>
    </button>
  );
}

/**
 * The live preview. Opens the camera on mount and releases it on unmount —
 * a camera light that stays on after the dialog closed is the kind of thing
 * a person notices and does not forgive.
 */
function CameraView({ onCapture, onUnavailable, busy }: {
  onCapture: (video: HTMLVideoElement) => void;
  onUnavailable: (why: string) => void;
  busy: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
      audio: false,
    }).then((s) => {
      if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
      stream = s;
      const v = videoRef.current;
      if (!v) return;
      v.srcObject = s;
      v.onloadedmetadata = () => { v.play().catch(() => undefined); setReady(true); };
    }).catch((err: unknown) => {
      const name = (err as { name?: string })?.name;
      onUnavailable(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'The browser did not allow the camera. Allow it for this site, or choose a file instead.'
          : name === 'NotFoundError' || name === 'OverconstrainedError'
            ? 'No camera was found on this device. Choose a file instead.'
            : 'The camera could not be started. Choose a file instead.',
      );
    });
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
    // The callback is the dialog's; the camera opens once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3">
      <div className="relative mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-card bg-neutral-900 ring-1 ring-neutral-200">
        {/* Mirrored like a mirror; the saved frame is not. */}
        <video ref={videoRef} playsInline muted className="h-full w-full -scale-x-100 object-cover" />
        {!ready && (
          <p className="absolute inset-0 flex items-center justify-center text-small text-neutral-200" role="status">
            Starting the camera…
          </p>
        )}
      </div>
      <div className="flex justify-center">
        <Button
          icon={<Camera className="h-4 w-4" />}
          loading={busy}
          disabled={!ready}
          onClick={() => { if (videoRef.current) onCapture(videoRef.current); }}
        >
          Capture
        </Button>
      </div>
    </div>
  );
}
